import { useState, useEffect } from 'react';
import { X, Share, Plus, Download, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export default function AddToHomeScreen() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed
    const standalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone 
      || document.referrer.includes('android-app://');
    setIsStandalone(standalone);

    // Detect iOS
    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(ios);

    // Listen for beforeinstallprompt event (Chrome/Android)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Show prompt after a delay (not immediately on page load)
      const hasSeenPrompt = localStorage.getItem('a2hs_dismissed');
      const lastDismissed = hasSeenPrompt ? parseInt(hasSeenPrompt) : 0;
      const daysSinceDismissed = (Date.now() - lastDismissed) / (1000 * 60 * 60 * 24);
      
      if (!hasSeenPrompt || daysSinceDismissed > 7) {
        setTimeout(() => setShowPrompt(true), 5000);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Show iOS prompt after delay
    if (ios && !standalone) {
      const hasSeenPrompt = localStorage.getItem('a2hs_dismissed');
      if (!hasSeenPrompt) {
        setTimeout(() => setShowPrompt(true), 5000);
      }
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('[PWA] User accepted install prompt');
    }
    
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    localStorage.setItem('a2hs_dismissed', Date.now().toString());
    setShowPrompt(false);
  };

  // Don't show if already installed
  if (isStandalone) return null;
  if (!showPrompt) return null;

  return (
    <div 
      className={cn(
        "fixed bottom-20 md:bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50",
        "bg-card border rounded-2xl shadow-2xl p-4 animate-slide-up"
      )}
    >
      <button 
        onClick={handleDismiss}
        className="absolute top-3 right-3 p-1 rounded-full hover:bg-muted"
      >
        <X className="h-4 w-4 text-muted-foreground" />
      </button>

      <div className="flex gap-4">
        <div className="flex-shrink-0 w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center">
          <Smartphone className="h-7 w-7 text-primary" />
        </div>
        
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-base mb-1">Add to Home Screen</h3>
          <p className="text-sm text-muted-foreground mb-3">
            Install Chain29 for faster ordering and offline access
          </p>

          {isIOS ? (
            <div className="space-y-2 text-sm">
              <p className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-xs">1</span>
                Tap <Share className="h-4 w-4 inline text-primary" /> Share button
              </p>
              <p className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-xs">2</span>
                Select <Plus className="h-4 w-4 inline" /> Add to Home Screen
              </p>
            </div>
          ) : (
            <div className="flex gap-2">
              <Button onClick={handleInstall} size="sm" className="gap-2">
                <Download className="h-4 w-4" />
                Install App
              </Button>
              <Button onClick={handleDismiss} variant="ghost" size="sm">
                Not Now
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
